import { takeWhile, switchMap, take, tap, map, catchError } from 'rxjs/operators';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { ModalService } from '@nimbus-library/features/modal/services/modal.service';
import { AlertType } from '@nimbus-library/features/modal/components/confirm-modal/confirm-modal.component';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { MicrosoftResource } from '@nimbus-library/features/authentication/models/graph-permission.enum';

@UntilDestroy()
export abstract class IPermissionService {
  private readonly REFRESH_DURATION = 10000;
  private childWindow: Window;
  private windowEventSubscription$: Subscription = Subscription.EMPTY;
  private permissionGranted$: Observable<boolean>;

  protected constructor(
    protected readonly windowEventsService: WindowEventsService,
    protected readonly authenticationService: IAuthenticationService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService,
  ) {}

  public abstract requestUserConsent(scopes: string[], redirectUrl?: string): void;
  public abstract requestAdminConsentLink(scopes: string[], redirectUrl?: string): string;

  protected openPermissionWindow(scopes: string[], redirectUrl: string, resource: MicrosoftResource = MicrosoftResource.GraphApi): void {
    if (this.childWindow && !this.childWindow.closed) {
      this.childWindow.close();
    }
    this.windowEventSubscription$.unsubscribe();
    this.windowEventSubscription$ = new Subscription();

    let isTokenValid = true;
    this.permissionGranted$ = this.backgroundTaskFactory.createIntervalTask(this.REFRESH_DURATION).pipe(
      switchMap(() => this.authenticationService.getApiToken$(true, scopes)),
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      take(6),
      map((token) => {
        const tokenPermissions = token ? token.permissions : [];
        const resourcePermissions = tokenPermissions.map((x) => `${resource}/${x.toLowerCase()}`);
        const requiredScopes = scopes.map((x) => x.toLowerCase());
        return requiredScopes.every((x) => resourcePermissions.includes(x));
      }),
      tap((isValid) => (isTokenValid = isValid)),
      takeWhile((isValid) => !isValid),
    );

    this.windowEventSubscription$ = this.authenticationService
      .grantExplicitConsent$(scopes, redirectUrl)
      .pipe(
        tap(() => {
          this.openLoadingModal();
        }),
        catchError((err) => {
          if (err?.message === 'CancelledByUser' && localStorage.getItem('grant_explicit_result') === 'OK') {
            localStorage.removeItem('grant_explicit_result');
            return of({});
          }
          return throwError(err);
        }),
        switchMap(() => this.permissionGranted$),
        untilDestroyed(this),
      )
      .subscribe({
        complete: () => {
          this.handleComplete(isTokenValid);
        },
      });
  }

  private openLoadingModal(): void {
    this.modalService
      .showLoadingPopup(AlertType.Information, this.translateService.instant(TranslationKeys.GeneralApplyingPermissions), '')
      .beforeClose.pipe(take(1), untilDestroyed(this))
      .subscribe(() => this.windowEventSubscription$.unsubscribe());
  }

  private handleComplete(valid: boolean): void {
    if (valid) {
      window.location.reload();
    } else {
      this.modalService.closeAll();
      this.modalService.showAlertPopup(
        AlertType.Error,
        this.translateService.instant(TranslationKeys.GeneralPermissionError),
        this.translateService.instant(TranslationKeys.GeneralPermissionsNotRefreshed),
      );
    }
  }
}
